import React, { useEffect, useRef, useState } from "react";
import {
  isImageUrlLoaded,
  markImageUrlLoaded,
  preloadImageUrl,
} from "../assetPreloader";

const DEFAULT_ROOT_MARGIN = "180px 0px";
const WARM_IMAGE_CACHE_LIMIT = 240;
const warmImageCache = new Map();

export function warmImages(urls) {
  (urls || []).forEach((url) => {
    if (!url) return;
    const existing = warmImageCache.get(url);
    if (existing) {
      warmImageCache.delete(url);
      warmImageCache.set(url, existing);
      return;
    }
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    if (typeof img.decode === "function") img.decode().catch(() => {});
    warmImageCache.set(url, img);
    if (warmImageCache.size > WARM_IMAGE_CACHE_LIMIT) {
      warmImageCache.delete(warmImageCache.keys().next().value);
    }
  });
}

function isImageWarm(src) {
  if (isImageUrlLoaded(src)) return true;
  const img = src ? warmImageCache.get(src) : null;
  return Boolean(img && img.complete && img.naturalWidth > 0);
}

function isNearViewport(node, rootMarginPx = 180) {
  if (!node || typeof node.getBoundingClientRect !== "function") return false;
  const rect = node.getBoundingClientRect();
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  return (
    rect.bottom >= -rootMarginPx &&
    rect.right >= -rootMarginPx &&
    rect.top <= vh + rootMarginPx &&
    rect.left <= vw + rootMarginPx
  );
}

// placeholderSrc (e.g. a cached thumbnail) shows until src has fully loaded
// and decoded, so upgrading to full-resolution art never flashes blank.
export default function AiLazyImage({
  src: fullSrc,
  placeholderSrc = null,
  alt = "",
  className = "",
  style,
  rootMargin = DEFAULT_ROOT_MARGIN,
  eager = false,
  draggable = false,
  decoding = "async",
  "aria-hidden": ariaHidden,
}) {
  const ref = useRef(null);
  const [, setUpgradeTick] = useState(0);
  const upgrading = Boolean(
    placeholderSrc && fullSrc && placeholderSrc !== fullSrc,
  );
  const fullReady = !upgrading || isImageWarm(fullSrc);
  const src = fullReady ? fullSrc : placeholderSrc;

  useEffect(() => {
    if (!upgrading || isImageWarm(fullSrc)) return undefined;
    let cancelled = false;
    preloadImageUrl(fullSrc).then(() => {
      if (!cancelled && isImageWarm(fullSrc)) setUpgradeTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [upgrading, fullSrc]);

  const [visible, setVisible] = useState(() =>
    Boolean(eager || !src || isImageWarm(src)),
  );
  const [currentSrc, setCurrentSrc] = useState(() =>
    src && (eager || isImageWarm(src)) ? src : null,
  );

  useEffect(() => {
    if (!src) {
      setCurrentSrc(null);
      setVisible(true);
      return undefined;
    }
    if (eager || isImageWarm(src)) {
      setVisible(true);
      setCurrentSrc(src);
      return undefined;
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver !== "function") {
      setVisible(true);
      setCurrentSrc(src);
      return undefined;
    }
    if (isNearViewport(node)) {
      setVisible(true);
      setCurrentSrc(src);
      return undefined;
    }
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || cancelled) return;
        setVisible(true);
        setCurrentSrc(src);
        observer.disconnect();
      },
      { root: null, rootMargin, threshold: 0.01 },
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [src, eager, rootMargin]);

  useEffect(() => {
    if (!visible || !src) return;
    setCurrentSrc(src);
  }, [src, visible]);

  const warm = isImageWarm(currentSrc);

  return (
    <img
      ref={ref}
      className={className}
      style={style}
      alt={alt}
      src={currentSrc || undefined}
      loading={eager || warm ? "eager" : "lazy"}
      decoding={warm ? "sync" : decoding}
      draggable={draggable}
      aria-hidden={ariaHidden}
      onLoad={(event) => {
        const loadedSrc = event.currentTarget.getAttribute("src");
        if (event.currentTarget.naturalWidth > 0) markImageUrlLoaded(loadedSrc);
      }}
    />
  );
}
